import { sizeText } from '../../shared/lib/format';
import type { TStatusGroup } from '../../shared/status/dictionary';
import type { TDryRun, TTransformVersionSummary } from './api';

export interface IPin {
  kind: 'flow';
  holderId: string;
  holderName: string;
  version: number;
  enabled: boolean;
  /** `live`, or `draft` when only its draft pins the version. */
  holderState: string;
}

export const pinTitle = (pin: IPin): string => pin.holderName || pin.holderId;

const distinct = (pins: readonly IPin[], kind: IPin['kind']): number =>
  new Set(pins.filter((pin) => pin.kind === kind).map((pin) => pin.holderId)).size;

export const holdersWords = (pins: readonly IPin[]): string => {
  const flows = distinct(pins, 'flow');
  return flows > 0 ? `${flows} flow${flows === 1 ? '' : 's'}` : 'nothing';
};

export interface IFlowPinRow {
  flowId: string;
  flowName: string;
  pinnedVersion: number | null;
  onlyInDraft: boolean;
}

export const pinsFromFlows = (rows: readonly IFlowPinRow[]): IPin[] =>
  rows.flatMap((row) =>
    row.pinnedVersion === null
      ? []
      : [
          {
            kind: 'flow' as const,
            holderId: row.flowId,
            holderName: row.flowName,
            version: row.pinnedVersion,
            enabled: true,
            holderState: row.onlyInDraft ? 'draft' : 'live',
          },
        ],
  );

export const pinsForVersion = (pins: IPin[], version: number): IPin[] =>
  pins.filter((pin) => pin.version === version);

export const isDraft = (version: number, latestVersion: number, pins: IPin[]): boolean =>
  version === latestVersion && pinsForVersion(pins, version).length === 0;

export const versionNote = (
  version: TTransformVersionSummary,
  latestVersion: number,
  pins: IPin[],
): string => {
  if (isDraft(version.version, latestVersion, pins)) return 'not pinned by any step yet';
  if (version.comment) return version.comment;
  // Pins are only known as they are now, so a version nothing pins was maybe never pinned.
  if (pinsForVersion(pins, version.version).length === 0) return 'not used';
  const holding = pinsForVersion(pins, version.version);
  return holding.every((pin) => pin.holderState === 'draft') ? 'pinned in a draft' : 'in use';
};

export const versionHolding = (version: number, pins: IPin[]): IPin[] =>
  pinsForVersion(pins, version);

export interface IRunOutcome {
  key: 'ran' | 'skipped' | 'timeout' | 'memory' | 'failed';
  group: TStatusGroup;
  label: string;
  meaning: string;
}

// `status` carries three values: a timeout is a `failed` with `TRANSFORM_TIMEOUT`.
export const runOutcome = (run: TDryRun): IRunOutcome => {
  if (run.status === 'ran') {
    return {
      key: 'ran',
      group: 'success',
      label: 'ran',
      meaning: 'On a real event this would be handed on to the destination.',
    };
  }
  if (run.status === 'dropped') {
    return {
      key: 'skipped',
      group: 'suppressed',
      label: 'skipped',
      meaning:
        'The handler returned nothing, so on a real event this one would be filtered and never sent. That is a decision, not a fault.',
    };
  }
  if (run.error?.code === 'TRANSFORM_TIMEOUT') {
    return {
      key: 'timeout',
      group: 'failure',
      label: 'timed out',
      meaning:
        'It was still running when the wall clock ran out and was interrupted. On a real event nothing would be delivered.',
    };
  }
  if (run.error?.code === 'TRANSFORM_OOM') {
    return {
      key: 'memory',
      group: 'failure',
      label: 'out of memory',
      meaning:
        'It asked for more memory than the sandbox allows and was stopped. On a real event nothing would be delivered.',
    };
  }
  return {
    key: 'failed',
    group: 'failure',
    label: 'failed',
    meaning: 'It threw before returning anything. On a real event nothing would be delivered.',
  };
};

export const loggedError = (run: TDryRun): boolean =>
  run.console.some((line) => line.level === 'error');

export const durationWords = (microseconds: number): string => {
  if (microseconds < 1000) return `${Math.round(microseconds)} µs`;
  const milliseconds = microseconds / 1000;
  if (milliseconds < 10) return `${milliseconds.toFixed(1)} ms`;
  return `${Math.round(milliseconds).toLocaleString('en-US')} ms`;
};

export const megabytes = (bytes: number): number => Math.round(bytes / (1024 * 1024));

export const memoryWords = (used: number | null | undefined, limit: number): string => {
  if (used === null || used === undefined) return `not measured · limit ${megabytes(limit)} MB`;
  const usedMb = used / (1024 * 1024);
  const shown = usedMb < 1 ? usedMb.toFixed(1) : String(Math.round(usedMb));
  return `${shown} MB of ${megabytes(limit)}`;
};

export const durationOfWords = (run: TDryRun): string =>
  `${durationWords(run.durationUs)} of ${run.wallClockMs.toLocaleString('en-US')} ms`;

// The one shared size format (00-rules), so code size reads like every other size.
export const bytesWords = (bytes: number): string => sizeText(bytes);
